import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getApiUrl, getCurrentUser, getIdToken, ReauthRequired } from "./auth";
import type { Team } from "./brain-types";

export type ProfileRole = "member" | "admin";

export interface UserProfile {
  userId: string;
  email: string;
  displayName: string;
  departments: Team[];
  role: ProfileRole;
  orgMember: boolean;
  /** Person slug (from /me) — identifies this user's own checkout/agent sessions. */
  personSlug?: string;
  labs?: boolean;
  jiraAccountId?: string;
  pinnedProjectIds?: string[];
  source: "me" | "claims";
  profileMissing?: boolean;
}

type ProfileStatus = "checking" | "anonymous" | "ready" | "error";

interface ProfileContextValue {
  status: ProfileStatus;
  profile: UserProfile | null;
  error: string | null;
  reload: () => void;
}

const ProfileContext = createContext<ProfileContextValue>({
  status: "checking",
  profile: null,
  error: null,
  reload: () => {},
});

const TEAMS: Team[] = ["devops", "dev", "product", "support", "sales", "finance"];

function decodeBase64Url(str: string): string {
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
}

function parseJwtPayload(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  if (!payload) return {};
  return JSON.parse(decodeBase64Url(payload)) as Record<string, unknown>;
}

function parseDepartments(value: unknown): Team[] {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  return raw
    .map((item) => String(item).trim().toLowerCase())
    .filter((item): item is Team => TEAMS.includes(item as Team));
}

function claimsToProfile(payload: Record<string, unknown>): UserProfile {
  const groups = Array.isArray(payload["cognito:groups"]) ? payload["cognito:groups"].map(String) : [];
  const roleClaim = String(payload["custom:role"] ?? payload["custom:brain_role"] ?? "").toLowerCase();
  const role: ProfileRole =
    roleClaim === "admin" || groups.some((group) => /admin|brain-admin/i.test(group)) ? "admin" : "member";
  const email = String(payload.email ?? "");
  const displayName = String(
    payload.name ??
      payload["preferred_username"] ??
      payload["cognito:username"] ??
      email ??
      "Aeronet user",
  );

  return {
    userId: String(payload.sub ?? payload["cognito:username"] ?? "unknown"),
    email,
    displayName,
    departments: parseDepartments(payload["custom:departments"] ?? payload["custom:teams"]),
    role,
    orgMember: payload["custom:org_member"] === "true" || groups.some((group) => /org/i.test(group)),
    labs: payload["custom:labs"] === "true",
    source: "claims",
  };
}

function mergeProfile(base: UserProfile, data: Partial<UserProfile> & Record<string, unknown>): UserProfile {
  return {
    ...base,
    userId: String(data.userId ?? data.user_id ?? base.userId),
    email: String(data.email ?? base.email),
    displayName: String(data.displayName ?? data.display_name ?? base.displayName),
    departments: parseDepartments(data.departments ?? base.departments),
    role: data.role === "admin" ? "admin" : "member",
    orgMember: Boolean(data.orgMember ?? data.org_member ?? base.orgMember),
    personSlug:
      (typeof data.personSlug === "string" && data.personSlug) ||
      (typeof data.person_slug === "string" && data.person_slug) ||
      base.personSlug,
    labs: Boolean(data.labs ?? base.labs),
    jiraAccountId: typeof data.jiraAccountId === "string" ? data.jiraAccountId : undefined,
    pinnedProjectIds: Array.isArray(data.pinnedProjectIds) ? data.pinnedProjectIds.filter((id): id is string => typeof id === "string") : [],
    source: "me",
    profileMissing: false,
  };
}

async function loadProfile(): Promise<UserProfile> {
  const token = await getIdToken();
  const claimsProfile = claimsToProfile(parseJwtPayload(token));
  const apiUrl = getApiUrl();

  const res = await fetch(`${apiUrl}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => null);

  if (!res || res.status === 404) {
    return { ...claimsProfile, profileMissing: true };
  }
  if (!res.ok) {
    throw new Error(`Profile load failed (${res.status})`);
  }

  const data = (await res.json()) as Partial<UserProfile> & Record<string, unknown>;
  return mergeProfile(claimsProfile, data.profile && typeof data.profile === "object" ? data.profile as Record<string, unknown> : data);
}

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [version, setVersion] = useState(0);
  const [status, setStatus] = useState<ProfileStatus>("checking");
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      // No stored token at all → anonymous (LoginPage redirects to SSO).
      if (!getCurrentUser()) {
        setStatus("anonymous");
        setProfile(null);
        setError(null);
        return;
      }

      setStatus("checking");
      setError(null);

      // loadProfile() refreshes the token if needed and tolerates a /me hiccup
      // (falls back to JWT claims). It throws ReauthRequired only when the
      // session is genuinely gone, and a plain Error on a transient/network blip.
      const attempt = () => loadProfile();
      try {
        const loaded = await attempt();
        if (cancelled) return;
        setProfile(loaded);
        setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ReauthRequired) {
          // Clean: send them through sign-in again (seamless via SSO).
          setProfile(null);
          setError(null);
          setStatus("anonymous");
          return;
        }
        // Transient — retry once before surfacing anything.
        try {
          await new Promise((r) => setTimeout(r, 800));
          if (cancelled) return;
          const loaded = await attempt();
          if (cancelled) return;
          setProfile(loaded);
          setStatus("ready");
        } catch (e2) {
          if (cancelled) return;
          if (e2 instanceof ReauthRequired) {
            setProfile(null);
            setError(null);
            setStatus("anonymous");
          } else {
            setProfile(null);
            setError(e2 instanceof Error ? e2.message : "Failed to load user profile.");
            setStatus("error");
          }
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [version]);

  const value = useMemo(
    () => ({
      status,
      profile,
      error,
      reload: () => setVersion((value) => value + 1),
    }),
    [error, profile, status],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  return useContext(ProfileContext);
}
